import { watch } from 'vue'
import { i18n } from '@/i18n.js'
import { createWebHistory, createRouter } from 'vue-router'
import { usePageLoading } from '@/composables/usePageLoading.js'
import { usePostHog } from '@/composables/usePostHog.js'
import { authGuard } from './guards.js'
import StepUpView from '../views/auth/StepUpView.vue'
import ImportView from '../views/ImportView.vue'
import ImportWizardView from '../views/ImportWizardView.vue'

import HomeView from '../views/HomeView.vue'

import ItemsView from '../views/ItemsView.vue'
import CategoriesView from '../views/CategoriesView.vue'
import CategoryDetailView from '../views/CategoryDetailView.vue'

import ItemView from '../views/ItemView.vue'
import AreasView from '../views/AreasView.vue'

import CustomerView from '../views/CustomerView.vue'
import CustomerDetailView from '../views/CustomerDetailView.vue'

import LocationsView from '../views/LocationsView.vue'
import CheckoutView from '../views/CheckoutView.vue'
import CheckoutsOverviewView from '../views/CheckoutsOverviewView.vue'

import LoginView from '../views/LoginView.vue'
import TenantSelectionView from '../views/TenantSelectionView.vue'
import TenantOidcView from '../views/TenantOidcView.vue'
import OidcCallbackView from '../views/OidcCallbackView.vue'
import PasswordResetRequestView from '../views/auth/PasswordResetRequestView.vue'
import PasswordResetConfirmView from '../views/auth/PasswordResetConfirmView.vue'
import ProjectPickView from '../views/ProjectPickView.vue'
import ProjectPersonCheckoutsView from '../views/ProjectPersonCheckoutsView.vue'
import ProjectsView from '../views/ProjectsView.vue'
import ProjectCalendarView from '../views/ProjectCalendarView.vue'
import ProjectView from '../views/ProjectView.vue'
import ProjectItemsView from '../views/ProjectItemsView.vue'
import ReturnView from '../views/ReturnView.vue'
import BundleDetailView from '../views/BundleDetailView.vue'
import ItemMetadataView from '../views/ItemMetadataView.vue'
import BundlesView from '../views/BundlesView.vue'
import BundleManageView from '../views/BundleManageView.vue'
import PriceGroupsView from '../views/PriceGroupsView.vue'
import PriceGroupDetailView from '../views/PriceGroupDetailView.vue'
import TransfersView from '../views/TransfersView.vue'
import TransferView from '../views/TransferView.vue'
import WikiPagesView from '../views/WikiPagesView.vue'
import WikiPageView from '../views/WikiPageView.vue'
import NotFoundView from '../views/NotFoundView.vue'
import ProfileView from '../views/ProfileView.vue'
import TenantUsersView from '../views/TenantUsersView.vue'
import TenantUserDetailView from '../views/TenantUserDetailView.vue'
import BrandingView from '../views/BrandingView.vue'
import ContractTemplateView from '../views/ContractTemplateView.vue'
import MaintenanceScheduleView from '../views/MaintenanceScheduleView.vue'
import MaintenanceScheduleFormView from '../views/MaintenanceScheduleFormView.vue'
import MaintenanceScheduleCompleteView from '../views/MaintenanceScheduleCompleteView.vue'
import ServiceHistoryView from '../views/ServiceHistoryView.vue'
import ServiceHistoryFormView from '../views/ServiceHistoryFormView.vue'
import AutomationsView from '../views/AutomationsView.vue'
import MacroManagerView from '../views/MacroManagerView.vue'
import MacroEditView from '../views/MacroEditView.vue'
import AssetValueView from '../views/AssetValueView.vue'
import DepreciationProfilesView from '../views/DepreciationProfilesView.vue'
import DocumentCenterView from '../views/DocumentCenterView.vue'
import ContractSignView from '../views/public/ContractSignView.vue'
import BulkEditHubView from '../views/BulkEditHubView.vue'
import { BULK_EDIT_RESOURCES } from '../config/bulkEditResources.js'

const routes = [
  {
    path: '/portal-admin',
    name: 'PortalAdminIndex',
    component: { render: () => null },
    meta: { titleKey: 'portal.management', requiredPermission: 'isOperatorOrAbove' },
    beforeEnter: async () => {
      const { useAuthStore } = await import('@/stores/auth.js')
      const slug = useAuthStore().currentTenant?.slug
      if (slug) return { path: `/portal-admin/${slug}/` }
      return { path: '/tenant' }
    },
  },
  {
    path: '/portal-admin/:tenant',
    component: () => import('../views/portal/PortalAdminView.vue'),
    meta: { titleKey: 'portal.management', requiredPermission: 'isOperatorOrAbove' },
    children: [
      { path: '', name: 'PortalAdminOverview', component: () => import('../views/portal/admin/PortalAdminOverview.vue'), meta: { titleKey: 'portal.overview', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'setup', name: 'PortalAdminSetup', component: () => import('../views/portal/admin/PortalAdminSetup.vue'), meta: { titleKey: 'portal.configuration', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'policies', name: 'PortalAdminPolicies', component: () => import('../views/portal/admin/PortalAdminPolicies.vue'), meta: { titleKey: 'portal.policiesTitle', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'features', name: 'PortalAdminFeatures', component: () => import('../views/portal/admin/PortalAdminFeatures.vue'), meta: { titleKey: 'portal.features', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'members', name: 'PortalAdminMembers', component: () => import('../views/portal/admin/PortalAdminMembers.vue'), meta: { titleKey: 'portal.members', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'organizations', name: 'PortalAdminOrganizations', component: () => import('../views/portal/admin/PortalAdminOrganizations.vue'), meta: { titleKey: 'portal.organizations', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'groups', name: 'PortalAdminGroups', component: () => import('../views/portal/admin/PortalAdminGroups.vue'), meta: { titleKey: 'portal.groups', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'endorsements', name: 'PortalAdminEndorsements', component: () => import('../views/portal/admin/PortalAdminEndorsements.vue'), meta: { titleKey: 'portal.endorsements', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'publication', name: 'PortalAdminPublication', component: () => import('../views/portal/admin/PortalAdminPublication.vue'), meta: { titleKey: 'portal.publication', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'domains', name: 'PortalAdminDomains', component: () => import('../views/portal/admin/PortalAdminDomains.vue'), meta: { titleKey: 'portal.domains', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'audit', name: 'PortalAdminAudit', component: () => import('../views/portal/admin/PortalAdminAudit.vue'), meta: { titleKey: 'portal.audit', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'requests', name: 'PortalAdminRequests', component: () => import('../views/portal/admin/PortalAdminRequests.vue'), meta: { titleKey: 'portal.requests', requiredPermission: 'isOperatorOrAbove' } },
      { path: 'crew', name: 'PortalAdminCrew', component: () => import('../views/portal/admin/PortalAdminCrew.vue'), meta: { titleKey: 'portal.crew', requiredPermission: 'isOperatorOrAbove' } },
    ],
  },
  { path: '/portal/:tenant?/:pathMatch(.*)*', name: 'PublicPortal', beforeEnter: to => { window.location.assign(to.fullPath); return false }, meta: { public: true, hideChrome: true, titleKey: 'portal.title' } },
  { path: '/bulk-edit', name: 'BulkEditHub', component: BulkEditHubView, meta: { titleKey: 'bulkEdit.title', requiredPermission: 'isBackOffice' } },
  ...Object.entries(BULK_EDIT_RESOURCES).map(([key, resource]) => ({
    path: `/bulk-edit/${key}`,
    name: `BulkEdit-${key}`,
    component: () => import('../views/BulkEditView.vue'),
    meta: { titleKey: 'bulkEdit.title', requiredPermission: resource.permission, bulkEditResource: key }
  })),
  { path: '/', name: 'Home', component: HomeView, meta: { titleKey: 'nav.home' } },
  { path: '/login', name: 'Login', component: LoginView, meta: { titleKey: 'login.signIn' } },
  {
    path: '/login/forgot',
    name: 'PasswordResetRequest',
    component: PasswordResetRequestView,
    meta: { public: true, hideChrome: true, titleKey: 'passwordReset.title' }
  },
  {
    path: '/login/reset',
    name: 'PasswordResetConfirm',
    component: PasswordResetConfirmView,
    meta: { public: true, hideChrome: true, titleKey: 'passwordReset.newPasswordTitle' }
  },
  {
    path: '/tenant',
    name: 'TenantSelection',
    component: TenantSelectionView,
    meta: { titleKey: 'ui.selectTenant' }
  },
  {
    path: '/tenant/oidc',
    name: 'TenantOidc',
    component: TenantOidcView,
    meta: { titleKey: 'login.signIn' }
  },
  {
    path: '/auth/callback',
    name: 'OidcCallback',
    component: OidcCallbackView,
    meta: { public: true }
  },
  { path: '/items', name: 'Items', component: ItemsView, meta: { titleKey: 'bulkEdit.resources.items', requiredPermission: 'isBackOffice' } },
  {
    path: '/import',
    name: 'Import',
    component: ImportView,
    meta: { adminOnly: true, titleKey: 'import.importItems' }
  },
  {
    path: '/import/:sessionId',
    name: 'ImportWizard',
    component: ImportWizardView,
    meta: { adminOnly: true, titleKey: 'ui.importWizard' }
  },
  {
    path: '/categories',
    name: 'Categories',
    component: CategoriesView,
    meta: { titleKey: 'entity.category_plural', requiredPermission: 'isManagerOrAbove' }
  },
  {
    path: '/categories/:categoryId',
    name: 'CategoryDetail',
    component: CategoryDetailView,
    meta: { titleKey: 'entity.category', requiredPermission: 'isManagerOrAbove' }
  },
  { path: '/item/new', name: 'ItemNew', component: ItemView, meta: { titleKey: 'common.newItem', requiredPermission: 'isOperatorOrAbove' } },
  { path: '/item/:itemId', name: 'Item', component: ItemView, meta: { titleKey: 'common.item' } },
  {
    path: '/instance/:instanceId',
    name: 'ItemInstance',
    component: ItemMetadataView,
    meta: { titleKey: 'macros.modelItemInstance' },
    beforeEnter: async (to) => {
      try {
        const { getItemInstance } = await import('@/api/api.js')
        const inst = await getItemInstance(to.params.instanceId)
        if (inst?.item?.item_type !== 2) {
          return { name: 'Item', params: { itemId: inst.item.id } }
        }
      } catch {
        // If lookup fails, allow through — ItemMetadataView handles the error
      }
    }
  },
  { path: '/areas', name: 'Areas', component: AreasView, meta: { titleKey: 'bulkEdit.resources.areas', requiredPermission: 'isBackOffice' } },
  { path: '/customers', name: 'Customers', component: CustomerView, meta: { titleKey: 'entity.customer_plural', requiredPermission: 'isManagerOrAbove' } },
  {
    path: '/customers/:customerId',
    name: 'CustomerDetail',
    component: CustomerDetailView,
    meta: { titleKey: 'entity.customer', requiredPermission: 'isManagerOrAbove' }
  },

  { path: '/locations', name: 'Locations', component: LocationsView, meta: { titleKey: 'bulkEdit.resources.locations', requiredPermission: 'isBackOffice' } },
  { path: '/checkout/', name: 'Checkout', component: CheckoutView, meta: { titleKey: 'entity.checkout', requiredPermission: 'isBackOffice' } },
  {
    path: '/checkouts',
    name: 'CheckoutsOverview',
    component: CheckoutsOverviewView,
    meta: { titleKey: 'entity.checkout_plural', requiredPermission: 'isBackOffice' }
  },

  // Flow 2: Warehouse Picking
  {
    path: '/projects/calendar',
    name: 'ProjectCalendar',
    component: ProjectCalendarView,
    meta: { titleKey: 'ui.projectCalendar', requiredPermission: 'isBackOffice' }
  },
  { path: '/projects', name: 'Projects', component: ProjectsView, meta: { titleKey: 'entity.project_plural', requiredPermission: 'isBackOffice' } },
  {
    path: '/project/new',
    name: 'ProjectNew',
    component: ProjectView,
    meta: { titleKey: 'projectView.newProject' }
  },
  {
    path: '/project/:projectId',
    name: 'ProjectDetail',
    component: ProjectView,
    meta: { titleKey: 'entity.project' }
  },
  {
    path: '/project/:projectId/items',
    name: 'ProjectItems',
    component: ProjectItemsView,
    meta: { titleKey: 'ui.projectItems' }
  },
  {
    path: '/project/:projectId/pick',
    name: 'ProjectPick',
    component: ProjectPickView,
    meta: { titleKey: 'projectPick.pickList' }
  },
  {
    path: '/project/:projectId/person-checkouts',
    name: 'ProjectPersonCheckouts',
    component: ProjectPersonCheckoutsView,
    meta: { titleKey: 'projects.personCheckouts' }
  },
  {
    path: '/project/:projectId/bundle/:bundleId',
    name: 'BundleDetail',
    component: BundleDetailView,
    meta: { titleKey: 'entity.bundle' }
  },

  // Flow 4: Returns
  { path: '/return', name: 'Return', component: ReturnView, meta: { titleKey: 'ui.return' } },

  // Bundle management
  { path: '/bundles', name: 'Bundles', component: BundlesView, meta: { titleKey: 'entity.bundle_plural', requiredPermission: 'isOperatorOrAbove' } },
  {
    path: '/bundles/new',
    name: 'BundleNew',
    component: BundleManageView,
    meta: { titleKey: 'ui.newBundle', requiredPermission: 'isManagerOrAbove' }
  },
  {
    path: '/bundles/:bundleId',
    name: 'BundleManage',
    component: BundleManageView,
    meta: { titleKey: 'entity.bundle', requiredPermission: 'isOperatorOrAbove' }
  },

  // Price groups
  {
    path: '/price-groups',
    name: 'PriceGroups',
    component: PriceGroupsView,
    meta: { titleKey: 'bulkEdit.resources.priceGroups', requiredPermission: 'isManagerOrAbove' }
  },
  {
    path: '/price-groups/:groupId',
    name: 'PriceGroupDetail',
    component: PriceGroupDetailView,
    meta: { titleKey: 'customers.priceGroup', requiredPermission: 'isManagerOrAbove' }
  },

  // Inventory transfers
  { path: '/transfers', name: 'Transfers', component: TransfersView, meta: { titleKey: 'nav.transfers', requiredPermission: 'isOperatorOrAbove' } },
  {
    path: '/transfers/:transferId',
    name: 'Transfer',
    component: TransferView,
    meta: { titleKey: 'transfers.singleTitle', requiredPermission: 'isOperatorOrAbove' }
  },

  // User profile / self-management
  { path: '/profile', name: 'Profile', component: ProfileView, meta: { titleKey: 'ui.profile' } },
  {
    path: '/profile/notifications',
    name: 'notification-preferences',
    component: () => import('@/views/NotificationPreferencesView.vue'),
    meta: { titleKey: 'profile.notificationPreferences' },
  },

  // Tenant admin pages
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: TenantUsersView,
    meta: { adminOnly: true, titleKey: 'bulkEdit.resources.users' }
  },
  {
    path: '/admin/users/:userId',
    name: 'AdminUserDetail',
    component: TenantUserDetailView,
    meta: { adminOnly: true, titleKey: 'macros.inputTypeUser' }
  },
  {
    path: '/admin/branding',
    name: 'AdminBranding',
    component: BrandingView,
    meta: { adminOnly: true, titleKey: 'nav.adminBranding' }
  },
  {
    path: '/admin/branding/contract-template',
    name: 'AdminContractTemplate',
    component: ContractTemplateView,
    meta: { adminOnly: true, titleKey: 'branding.contractTemplate' }
  },
  {
    path: '/admin/macros',
    name: 'MacroManager',
    component: MacroManagerView,
    meta: { adminOnly: true, titleKey: 'nav.adminMacros' }
  },
  {
    path: '/admin/macros/:macroId',
    name: 'MacroEdit',
    component: MacroEditView,
    meta: { adminOnly: true, titleKey: 'ui.macro' }
  },
  {
    path: '/admin/asset-values',
    name: 'AssetValues',
    component: AssetValueView,
    meta: { adminOnly: true, titleKey: 'nav.assetValues' }
  },
  {
    path: '/admin/depreciation-profiles',
    name: 'DepreciationProfiles',
    component: DepreciationProfilesView,
    meta: { adminOnly: true, titleKey: 'nav.depreciationProfiles' }
  },

  // Automations (operators and above can run macros; admins create them)
  {
    path: '/automations',
    name: 'Automations',
    component: AutomationsView,
    meta: { titleKey: 'nav.automations', requiredPermission: 'isOperatorOrAbove' }
  },

  // Maintenance
  {
    path: '/maintenance/schedules',
    name: 'MaintenanceSchedules',
    component: MaintenanceScheduleView,
    meta: { titleKey: 'ui.maintenanceSchedules', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/schedules/new',
    name: 'MaintenanceScheduleNew',
    component: MaintenanceScheduleFormView,
    meta: { titleKey: 'maintenance.form.newTitle', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/schedules/:id/edit',
    name: 'MaintenanceScheduleEdit',
    component: MaintenanceScheduleFormView,
    meta: { titleKey: 'ui.editMaintenanceSchedule', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/schedules/:id/complete',
    name: 'MaintenanceScheduleComplete',
    component: MaintenanceScheduleCompleteView,
    meta: { titleKey: 'maintenance.complete.title', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/history',
    name: 'ServiceHistory',
    component: ServiceHistoryView,
    meta: { titleKey: 'maintenance.serviceHistory', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/history/new',
    name: 'ServiceHistoryNew',
    component: ServiceHistoryFormView,
    meta: { titleKey: 'serviceHistory.newTitle', requiredPermission: 'isOperatorOrAbove' }
  },
  {
    path: '/maintenance/history/:id/edit',
    name: 'ServiceHistoryEdit',
    component: ServiceHistoryFormView,
    meta: { titleKey: 'serviceHistory.editTitle', requiredPermission: 'isOperatorOrAbove' }
  },

  // Document center
  {
    path: '/documents',
    name: 'DocumentCenter',
    component: DocumentCenterView,
    meta: { titleKey: 'ui.documentCenter', requiredPermission: 'isBackOffice' }
  },

  // Wiki pages
  { path: '/wiki', name: 'WikiPages', component: WikiPagesView, meta: { titleKey: 'nav.wiki', requiredPermission: 'isBackOffice' } },
  {
    path: '/wiki/new',
    name: 'WikiPageNew',
    component: WikiPageView,
    meta: { titleKey: 'wiki.newTitle', requiredPermission: 'isManagerOrAbove' }
  },
  {
    path: '/wiki/:wikiId',
    name: 'WikiPage',
    component: WikiPageView,
    meta: { titleKey: 'itemView.wikiPage', requiredPermission: 'isBackOffice' }
  },

  // Slug shortcut: app.eqsys.no/tenant/<tenant-slug> — resolves to LoginView with tenant pre-seeded.
  {
    path: '/tenant/:tenantSlug',
    name: 'TenantSlugLogin',
    component: LoginView,
    meta: { public: true, hideChrome: true, titleKey: 'login.signIn' }
  },

  // Public contract signing (unauthenticated — token is the credential)
  {
    path: '/sign/:token',
    name: 'ContractSign',
    component: ContractSignView,
    meta: { public: true, hideChrome: true, titleKey: 'projectView.signContract' }
  },

  // Catch-all 404 for unmatched paths (multi-segment or truly unknown routes).
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: { public: true, titleKey: 'ui.notFound' }
  },

  // Step-up authentication (AAL elevation)
  {
    path: '/auth/step-up',
    name: 'StepUp',
    component: StepUpView,
    meta: { public: true, hideChrome: true, titleKey: 'ui.verifyIdentity' }
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard — delegates auth + admin checks to guards.js
router.beforeEach(async (to) => {
  const { start } = usePageLoading()
  start()
  if (to.path === '/') {
    const { useAuthStore } = await import('@/stores/auth.js')
    if (!useAuthStore().isAuthenticated) {
      try {
        const base = (import.meta.env.VITE_API_BASE_URL || '/api/').replace(/\/?$/, '/')
        const response = await fetch(`${base}public/portal/v2/bootstrap/`, { credentials: 'omit' })
        if (response.ok) { const portal = await response.json(); window.location.replace(`/portal/${portal.tenant}/`); return false }
      } catch { /* Normal application authentication handles non-portal hosts. */ }
    }
  }
  return authGuard(to)
})

router.afterEach((to) => {
  const { finish } = usePageLoading()
  finish()
  updateDocumentTitle(to)
  // Track pageview — no-op when the user has not given consent
  const { capturePageview } = usePostHog()
  capturePageview(window.location.href)
})

function updateDocumentTitle(route) {
  const key = route.meta?.titleKey
  if (!key) { document.title = 'eqSys'; return }
  let title = i18n.global.t(key)
  if (route.meta.bulkEditResource) title += ': ' + i18n.global.t(`bulkEdit.resources.${route.meta.bulkEditResource}`)
  document.title = `${title} | eqSys`
}

watch(i18n.global.locale, () => updateDocumentTitle(router.currentRoute.value))
